// Assinatura com o dedo: o quadro para assinar, o desenho guardado como
// caminho SVG (traços numa caixa de 600 x 200) e o código do link de
// assinatura, que vai para o banco só como sha256.
export const LARGURA = 600;
export const ALTURA = 200;

// ---------------------------------------------------------------------
// Caminho ⇄ traços
// ---------------------------------------------------------------------
// [[[x, y], [x, y]…], …] → "M10 20L12 22…M…"
export function caminhoDosTracos(tracos) {
  return tracos
    .filter((t) => t.length > 0)
    .map((t) => {
      const pontos = t.length === 1 ? [t[0], t[0]] : t; // um toque vira um ponto
      return pontos.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${Math.round(x)} ${Math.round(y)}`).join('');
    })
    .join('');
}

export function tracosDoCaminho(caminho) {
  const tracos = [];
  const re = /([ML])\s*(-?\d+(?:\.\d+)?)[\s,]+(-?\d+(?:\.\d+)?)/g;
  for (let m = re.exec(caminho ?? ''); m; m = re.exec(caminho)) {
    const ponto = [Number(m[2]), Number(m[3])];
    if (m[1] === 'M' || tracos.length === 0) tracos.push([ponto]);
    else tracos[tracos.length - 1].push(ponto);
  }
  return tracos;
}

// Tem traço suficiente para ser uma assinatura (e não um toque sem querer)?
export function assinaturaSuficiente(tracos) {
  let comprimento = 0;
  for (const t of tracos) {
    for (let i = 1; i < t.length; i++) comprimento += Math.hypot(t[i][0] - t[i - 1][0], t[i][1] - t[i - 1][1]);
  }
  return comprimento >= 60;
}

// ---------------------------------------------------------------------
// Na tela
// ---------------------------------------------------------------------
const SVG = 'http://www.w3.org/2000/svg';

// Desenho da assinatura guardada (sem innerHTML: o caminho vira atributo).
export function desenhoDaAssinatura(caminho, { classe = 'assinatura-desenho' } = {}) {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', `0 0 ${LARGURA} ${ALTURA}`);
  svg.setAttribute('class', classe);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', 'Assinatura');
  const path = document.createElementNS(SVG, 'path');
  path.setAttribute('d', caminho);
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '4');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.append(path);
  return svg;
}

// Quadro para assinar com o dedo (ou o mouse). Devolve { elemento, limpar,
// tracos, aoMudar }.
export function quadroDeAssinatura() {
  const tela = document.createElement('canvas');
  tela.className = 'assinatura-quadro';
  tela.setAttribute('aria-label', 'Quadro para assinar com o dedo');
  tela.setAttribute('role', 'img');
  const tracos = [];
  let atual = null;
  let aoMudar = () => {};

  const ajustar = () => {
    const escala = window.devicePixelRatio || 1;
    const largura = tela.clientWidth || LARGURA;
    tela.width = Math.round(largura * escala);
    tela.height = Math.round((largura * ALTURA / LARGURA) * escala);
    redesenhar();
  };

  const contexto = () => {
    const c = tela.getContext('2d');
    const k = tela.width / LARGURA;
    c.setTransform(k, 0, 0, k, 0, 0);
    c.lineWidth = 4;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    c.strokeStyle = getComputedStyle(tela).color || '#111';
    return c;
  };

  function redesenhar() {
    const c = contexto();
    c.save();
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.clearRect(0, 0, tela.width, tela.height);
    c.restore();
    for (const t of tracos) {
      c.beginPath();
      c.moveTo(t[0][0], t[0][1]);
      if (t.length === 1) c.lineTo(t[0][0] + 0.1, t[0][1]); // um toque vira um ponto
      for (const [x, y] of t.slice(1)) c.lineTo(x, y);
      c.stroke();
    }
  }

  const ponto = (evento) => {
    const r = tela.getBoundingClientRect();
    return [
      Math.max(0, Math.min(LARGURA, ((evento.clientX - r.left) / r.width) * LARGURA)),
      Math.max(0, Math.min(ALTURA, ((evento.clientY - r.top) / r.height) * ALTURA)),
    ];
  };

  tela.addEventListener('pointerdown', (evento) => {
    evento.preventDefault();
    tela.setPointerCapture?.(evento.pointerId);
    atual = [ponto(evento)];
    tracos.push(atual);
    redesenhar();
  });
  tela.addEventListener('pointermove', (evento) => {
    if (!atual) return;
    evento.preventDefault();
    const [x, y] = ponto(evento);
    const [ux, uy] = atual[atual.length - 1];
    if (Math.hypot(x - ux, y - uy) < 2) return; // menos pontos, mesmo desenho
    atual.push([x, y]);
    const c = contexto();
    c.beginPath();
    c.moveTo(ux, uy);
    c.lineTo(x, y);
    c.stroke();
  });
  const terminar = () => {
    if (!atual) return;
    atual = null;
    aoMudar();
  };
  tela.addEventListener('pointerup', terminar);
  tela.addEventListener('pointercancel', terminar);
  // Sem isto, o dedo arrastando rola a tela em vez de desenhar.
  tela.style.touchAction = 'none';

  requestAnimationFrame(ajustar);
  window.addEventListener('resize', ajustar);

  return {
    elemento: tela,
    tracos: () => tracos.map((t) => t.slice()),
    limpar: () => {
      tracos.length = 0;
      redesenhar();
      aoMudar();
    },
    aoMudar: (fn) => {
      aoMudar = fn;
    },
    desligar: () => window.removeEventListener('resize', ajustar),
  };
}

// ---------------------------------------------------------------------
// Link de assinatura
// ---------------------------------------------------------------------
// Código aleatório de 22 letras (128 bits), bom para ir num link.
export function novoCodigo() {
  const b = crypto.getRandomValues(new Uint8Array(16));
  let bin = '';
  for (const x of b) bin += String.fromCharCode(x);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// sha256 em hexadecimal. Usa o do navegador quando existe (https ou
// localhost); na rede local sem https, faz a conta aqui mesmo.
export async function sha256Hex(texto) {
  const bytes = new TextEncoder().encode(texto);
  if (typeof crypto !== 'undefined' && crypto.subtle?.digest) {
    const hash = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(hash)].map((x) => x.toString(16).padStart(2, '0')).join('');
  }
  return sha256Js(bytes);
}

// Constantes do SHA-256: parte fracionária das raízes quadradas (H) e cúbicas
// (K) dos primeiros números primos.
const PRIMOS = [];
for (let n = 2; PRIMOS.length < 64; n++) if (PRIMOS.every((p) => n % p)) PRIMOS.push(n);
const frac32 = (x) => Math.floor((x - Math.floor(x)) * 2 ** 32) >>> 0;
const H0 = PRIMOS.slice(0, 8).map((p) => frac32(Math.sqrt(p)));
const K = PRIMOS.map((p) => frac32(Math.cbrt(p)));

export function sha256Js(bytes) {
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  const tamanho = Math.ceil((bytes.length + 9) / 64) * 64;
  const m = new Uint8Array(tamanho);
  m.set(bytes);
  m[bytes.length] = 0x80;
  const dv = new DataView(m.buffer);
  const bits = bytes.length * 8;
  dv.setUint32(tamanho - 8, Math.floor(bits / 2 ** 32));
  dv.setUint32(tamanho - 4, bits >>> 0);
  const h = H0.slice();
  const w = new Uint32Array(64);
  for (let off = 0; off < tamanho; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    [a, b, c, d, e, f, g, hh].forEach((x, i) => {
      h[i] = (h[i] + x) >>> 0;
    });
  }
  return h.map((x) => x.toString(16).padStart(8, '0')).join('');
}
